/**
 * The browser half of passkey 2FA (issue #15).
 *
 * WebAuthn talks in `ArrayBuffer`s and JSON does not, so the API sends the
 * buffer-shaped fields as base64url and this converts them back on the way in
 * and forward again on the way out. Nothing here decides anything: the
 * challenge, the timeout and the acceptable credentials are all the API's, and
 * a client that changed any of them would only be failing the check at the
 * other end.
 *
 * Written out by hand rather than leaning on `PublicKeyCredential.toJSON()` and
 * its `parse*FromJSON()` companions. Those do exactly this and are the obvious
 * answer — but they are recent enough that an iPad a year or two old has the
 * passkey and not the helper, and a second factor that silently fails to enrol
 * on the device somebody actually walks around the theatre with is worse than
 * thirty lines of encoding.
 */

/**
 * Whether this browser can do WebAuthn at all. False on anything old, and on
 * any page not served from a secure context — `localhost` counts as one, so
 * the dev stack is fine over plain http.
 */
export function passkeysSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator.credentials?.create === 'function'
  )
}

/**
 * Thrown when the person, rather than the credential, is the reason nothing
 * happened — they dismissed the prompt, or let it time out. Worth telling apart
 * because it is not an error to report, just a thing that did not happen.
 */
export class PasskeyCancelled extends Error {
  constructor() {
    super('Passkey prompt dismissed.')
    this.name = 'PasskeyCancelled'
  }
}

function toBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)

  return bytes.buffer
}

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  for (const byte of bytes) binary += String.fromCharCode(byte)

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The fields WebAuthn wants as buffers, wherever they turn up. */
interface CredentialDescriptorJSON {
  id: string
  type: string
  transports?: AuthenticatorTransport[]
}

function toDescriptors(value: unknown): PublicKeyCredentialDescriptor[] {
  if (!Array.isArray(value)) return []

  return (value as CredentialDescriptorJSON[]).map((descriptor) => ({
    id: toBuffer(descriptor.id),
    type: 'public-key',
    ...(descriptor.transports === undefined ? {} : { transports: descriptor.transports }),
  }))
}

/**
 * Asks the authenticator to make a new key. Answers the credential in the JSON
 * form the API reads, or null if the prompt was dismissed.
 */
export async function createPasskey(
  publicKey: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const options = publicKey as unknown as PublicKeyCredentialCreationOptions & {
    challenge: string
    user: { id: string; name: string; displayName: string }
    excludeCredentials?: unknown
  }

  const credential = await request(() =>
    navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: toBuffer(options.challenge),
        user: { ...options.user, id: toBuffer(options.user.id) },
        excludeCredentials: toDescriptors(options.excludeCredentials),
      },
    }),
  )

  const response = credential.response as AuthenticatorAttestationResponse

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      attestationObject: toBase64Url(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
  }
}

/** Asks the authenticator to sign the challenge with a key it already holds. */
export async function assertPasskey(
  publicKey: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const options = publicKey as unknown as PublicKeyCredentialRequestOptions & {
    challenge: string
    allowCredentials?: unknown
  }

  const credential = await request(() =>
    navigator.credentials.get({
      publicKey: {
        ...options,
        challenge: toBuffer(options.challenge),
        allowCredentials: toDescriptors(options.allowCredentials),
      },
    }),
  )

  const response = credential.response as AuthenticatorAssertionResponse

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      authenticatorData: toBase64Url(response.authenticatorData),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle === null ? null : toBase64Url(response.userHandle),
    },
  }
}

/**
 * The part both ceremonies share: run the call, and turn the two ways it can
 * come back with nothing into something the screen can act on.
 *
 * A dismissed prompt and a timeout both arrive as `NotAllowedError`, which is
 * deliberate on the browser's part — telling them apart would say whether the
 * key was present. Neither is an error worth reporting, so both become
 * `PasskeyCancelled` and the screen simply offers to try again.
 */
async function request(call: () => Promise<Credential | null>): Promise<PublicKeyCredential> {
  let credential: Credential | null

  try {
    credential = await call()
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      throw new PasskeyCancelled()
    }

    throw error
  }

  if (credential === null) throw new PasskeyCancelled()

  return credential as PublicKeyCredential
}
